/**
 * Build an insertable copy of an itinerary item that keeps every detail
 * (time, website link, photos, inclusions, pricing choice, markup…) instead of
 * a hand-picked field list that silently drops newer columns.
 */
const DROP_KEYS = [
  "id",
  "created_at",
  "quotation_id",
  // joined / enriched objects that are not columns
  "linked_product",
  "products",
  "suppliers",
  "room_rate",
  "lead_item_suppliers",
  "itinerary_item_actuals",
] as const;

export function copyItineraryItem<T extends Record<string, any>>(
  item: T,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...item };
  for (const key of DROP_KEYS) delete copy[key];
  // Drop any other joined arrays/objects that slipped in from selects.
  for (const [key, value] of Object.entries(copy)) {
    // UI lists attach callbacks (e.g. onRequestQuote) to items; they are never columns.
    if (typeof value === "function" || value === undefined) {
      delete copy[key];
      continue;
    }
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      key !== "selected_time_slot"
    ) {
      // Only selected_time_slot is a JSON object column on itinerary_items.
      delete copy[key];
    }
  }
  return { ...copy, ...overrides };
}

/**
 * Copy a whole itinerary to another lead. Main items are returned first and
 * alternatives second, with `alternative_to_item_id` remapped to the new ids.
 */
export function copyItineraryForLead<T extends Record<string, any>>(
  items: T[],
  overrides: Record<string, unknown>,
  newId: () => string = () => crypto.randomUUID(),
): { mains: Record<string, unknown>[]; alternatives: Record<string, unknown>[] } {
  const idMap = new Map<string, string>();
  for (const item of items) idMap.set(item.id, newId());
  const mains: Record<string, unknown>[] = [];
  const alternatives: Record<string, unknown>[] = [];
  for (const item of items) {
    const row = copyItineraryItem(item, { ...overrides, id: idMap.get(item.id) });
    if (item.linked_option_item_id) row.linked_option_item_id = idMap.get(item.linked_option_item_id) ?? null;
    const altOf = item.alternative_to_item_id as string | null | undefined;
    if (altOf) {
      row.alternative_to_item_id = idMap.get(altOf) ?? null;
      if (!row.alternative_to_item_id) row.pricing_treatment = "included";
      alternatives.push(row);
    } else {
      mains.push(row);
    }
  }
  return { mains, alternatives };
}
